import { MaximizeIcon } from '@assets/icons';
import { IconButton } from '@components/button';
import { Image } from '@components/image';
import { OptionsMenu } from '@components/menu';
import {
  useEditWorkspaceThumbnailMutation,
  useGetDatasetThumbnailsQuery,
} from '@generated/graphql';
import { Stack, useTheme } from '@mui/material';
import { useNotifications } from '@notifications/Notifications';
import { useState } from 'react';
import { FullScreenImageModal } from '../libraries/datasets/DatasetsDetailsImagesSlider/FullScreenImageModal';

type WorkspaceRecentDatasetsImagesSliderProps = {
  workspaceId: string;
  datasetId: string;
};

export const WorkspaceRecentDatasetsImagesSlider = ({
  workspaceId,
  datasetId,
}: WorkspaceRecentDatasetsImagesSliderProps) => {
  const { palette } = useTheme();
  const { data } = useGetDatasetThumbnailsQuery({ variables: { workspaceId, datasetId } });
  const thumbnails = data?.getDatasetThumbnails || [];
  const { createAsyncNotification } = useNotifications();
  const [editWorkspaceThumbnail] = useEditWorkspaceThumbnailMutation();

  const handleSetThumbnail = createAsyncNotification(
    'You have successfully changed the workspace thumbnail',
    async (url: string) => {
      await editWorkspaceThumbnail({
        variables: {
          workspaceId,
          thumbnail: url,
        },
      });
    },
  );

  const [imageIndex, setImageIndex] = useState(0);

  const [fullScreenImageModalOpen, setFullScreenImageModalOpen] = useState(false);

  const handleNextClick = () => {
    if (!thumbnails) return;
    if (imageIndex < thumbnails.length - 1) {
      setImageIndex(imageIndex + 1);
    }
  };

  return (
    <Stack sx={{ height: '100%', width: '100%', position: 'relative' }}>
      <Image
        url={thumbnails?.[imageIndex]}
        onPrevClick={imageIndex > 0 ? () => setImageIndex(imageIndex - 1) : undefined}
        onNextClick={handleNextClick}
      />
      <IconButton
        Icon={MaximizeIcon}
        sx={{
          position: 'absolute',
          top: 8,
          right: 32,
          borderRadius: '20px',
          backgroundColor: palette.background.default,
        }}
        onClick={() => setFullScreenImageModalOpen(true)}
      />
      <FullScreenImageModal
        url={thumbnails?.[imageIndex]}
        open={fullScreenImageModalOpen}
        onClose={() => setFullScreenImageModalOpen(false)}
      />
      <OptionsMenu
        sx={{
          position: 'absolute',
          right: 0,
          top: 8,
          borderRadius: '20px',
          backgroundColor: palette.background.default,
        }}
        items={[
          {
            label: 'Set workspace thumbnail',
            onClick: () => handleSetThumbnail(thumbnails?.[imageIndex]),
          },
        ]}
        menuProps={{ transformOrigin: { vertical: 'top', horizontal: 'right' } }}
      />
    </Stack>
  );
};
